import { logger } from '../logger';

class NotificationService {
  private static instance: NotificationService;
  private audio: HTMLAudioElement | null = null;
  private isInitialized: boolean = false;
  private isEnabled: boolean = true;
  private initPromise: Promise<void> | null = null;
  private userInteracted: boolean = false;

  private constructor() {
    // Initialisation différée
    this.initPromise = this.initialize();
    this.setupUserInteractionListener();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private setupUserInteractionListener(): void {
    // Marquer que l'utilisateur a interagi avec la page
    const markInteracted = () => {
      this.userInteracted = true;
      // Réessayer de jouer un son silencieux pour débloquer l'audio
      this.tryUnlockAudio();
    };

    // Écouter les interactions utilisateur
    window.addEventListener('click', markInteracted, { once: true });
    window.addEventListener('touchstart', markInteracted, { once: true });
    window.addEventListener('keydown', markInteracted, { once: true });
  }

  private async tryUnlockAudio(): Promise<void> {
    try {
      if (this.audio) {
        this.audio.volume = 0;
        await this.audio.play();
        await this.audio.pause();
        this.audio.currentTime = 0;
        this.audio.volume = 0.5;
      }
    } catch (error) {
      logger.warn('Could not unlock audio', {
        category: 'Audio',
        data: { error }
      });
    }
  }

  private async initialize(): Promise<void> {
    try {
      // Créer l'élément audio avec un son de notification court
      this.audio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
      
      if (this.audio) {
        // Configurer l'audio
        this.audio.volume = 0.5;
        this.audio.preload = 'auto';
        
        // Attendre que l'audio soit chargé
        await new Promise<void>((resolve, reject) => {
          if (!this.audio) {
            reject(new Error('Audio not initialized'));
            return;
          }

          const handleCanPlay = () => {
            this.isInitialized = true;
            resolve();
          };

          const handleError = (e: Event) => {
            reject(new Error(`Audio load error: ${e.type}`));
          };

          this.audio.addEventListener('canplaythrough', handleCanPlay, { once: true });
          this.audio.addEventListener('error', handleError, { once: true });

          // Forcer le chargement
          this.audio.load();
        });

      }
    } catch (error) {
      logger.error('Failed to initialize notification sound', {
        category: 'Audio',
        data: { error }
      });
      throw error;
    }
  }

  public async play(): Promise<void> {
    try {
      // Attendre l'initialisation si nécessaire
      if (this.initPromise) {
        await this.initPromise;
      }

      if (!this.isInitialized || !this.isEnabled || !this.audio) {
        logger.warn('Cannot play notification: not ready', {
          category: 'Audio',
          data: {
            initialized: this.isInitialized,
            enabled: this.isEnabled,
            hasAudio: !!this.audio
          }
        });
        return;
      }

      // Si l'utilisateur n'a pas encore interagi, on ne joue pas le son
      if (!this.userInteracted) {
        logger.warn('Cannot play notification: no user interaction yet', {
          category: 'Audio'
        });
        return;
      }

      // Réinitialiser l'audio au début
      this.audio.currentTime = 0;
      
      // Jouer le son avec gestion des erreurs
      try {
        const playPromise = this.audio.play();
        if (playPromise) {
          await playPromise;
        }
      } catch (error) {
        if ((error as Error).name === 'NotAllowedError') {
          logger.warn('Autoplay prevented by browser', {
            category: 'Audio',
            data: { error }
          });
          // Essayer de débloquer l'audio
          await this.tryUnlockAudio();
        } else {
          throw error;
        }
      }
    } catch (error) {
      logger.error('Failed to play notification sound', {
        category: 'Audio',
        data: { error }
      });
    }
  }

  public enable(): void {
    this.isEnabled = true;
  }

  public disable(): void {
    this.isEnabled = false;
  }

  public isSupported(): boolean {
    return this.isInitialized && !!this.audio;
  }
}

export const notificationService = NotificationService.getInstance();